import { PAYMENT_NETWORK_ACH, PAYMENT_NETWORK_WIRE, type BankPaymentNetwork } from '@klear/bank-account/constants'
import { validateStatementDescriptor } from '@klear/bank-account/statement-descriptor'
import type { InboundTransferCreateWire, OutboundTransferCreateWire } from '@klear/bank-transfer/bank-transfer.wire'
import { Money } from '@klear/money'
import values from 'lodash/values'

export type TransferFormErrorMap = Record<string, string | undefined>

export type InboundTransferForm = {
  traderId: number
  external_bank_account_id: number
  amount: Money
  statement_descriptor?: string
  klear_account_id?: number
  note?: string
}

export type OutboundTransferForm = Omit<InboundTransferForm, 'traderId' | 'note'> & {
  network: BankPaymentNetwork
}

function validateExternalBankAccountId(id: number, errors: TransferFormErrorMap): void {
  if (id < 1) {
    errors.external_bank_account_id = 'Select a bank account'
  }
}

function validateStatementDescriptorField(
  reference: string | undefined,
  network: BankPaymentNetwork,
  errors: TransferFormErrorMap
): void {
  const trimmed = reference?.trim()
  if (!trimmed) return
  const result = validateStatementDescriptor(trimmed, network)
  if (!result.ok) {
    errors.statement_descriptor = result.message
  }
}

export function transferFormHasErrors(errors: TransferFormErrorMap): boolean {
  return values(errors).some((message) => message != null && message.length > 0)
}

export function validateInboundTransferForm(form: InboundTransferForm): TransferFormErrorMap {
  const errors: TransferFormErrorMap = {}
  validateExternalBankAccountId(form.external_bank_account_id, errors)
  validateStatementDescriptorField(form.statement_descriptor, PAYMENT_NETWORK_ACH, errors)
  return errors
}

export function validateOutboundTransferForm(form: OutboundTransferForm): TransferFormErrorMap {
  const errors: TransferFormErrorMap = {}
  validateExternalBankAccountId(form.external_bank_account_id, errors)
  if (form.network !== PAYMENT_NETWORK_ACH && form.network !== PAYMENT_NETWORK_WIRE) {
    errors.network = 'Select a payment network'
  }
  validateStatementDescriptorField(form.statement_descriptor, form.network, errors)
  return errors
}

export function inboundTransferFormToWire(form: InboundTransferForm): InboundTransferCreateWire {
  const trimmed = form.statement_descriptor?.trim()
  return {
    traderId: form.traderId,
    external_bank_account_id: form.external_bank_account_id,
    amount: form.amount.toJSON(),
    statement_descriptor: trimmed || undefined,
    klear_account_id: form.klear_account_id ?? undefined,
    note: form.note?.trim() || undefined
  }
}

export function outboundTransferFormToWire(form: OutboundTransferForm): OutboundTransferCreateWire {
  const trimmed = form.statement_descriptor?.trim()
  return {
    external_bank_account_id: form.external_bank_account_id,
    amount: form.amount.toJSON(),
    network: form.network,
    statement_descriptor: trimmed || undefined,
    klear_account_id: form.klear_account_id ?? undefined
  }
}
